import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, ListGroup, Nav, Row, Spinner } from 'react-bootstrap-5';
import { deleteContact, deleteContacts, searchContacts } from '../../helpers/prospect_api';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDownAZ, faArrowUpAZ, faTimes,
  faCircleExclamation, faMagnifyingGlass,
  faTrash, faPlus, faUserPlus, faTag,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';
import ContactDetails from './contact_details';
import TagPicker from '../tags/tag_picker';
import Paginator from '../paginator';
import EmptyState from '../empty_state';
import { useMediaQuery } from 'react-responsive';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import CreateModal from './create_modal';
import { CONTACT_MODEL } from '../../constants';
import pluralize from 'pluralize';
import ConnectTagManager from '../tags/connect_tag_manager';
import { createPortal } from 'react-dom';
import ImportHandler from './import_handler';
import { CONNECT_COMPANY_TYPE, CONNECT_PERSON_TYPE, type Company, type Contact, type Profile } from 'bundles/connect/types';

const TAB_CONTACTS = 'contacts';
const TAB_COMPANIES = 'companies';
const PER_PAGE = 20;

type Props = {
  initialContactId?: number;
  profile: Profile;
}


export default function ContactsIndex({ initialContactId, profile }: Props) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState(initialContactId);
  const [checkedContactIds, setCheckedContactIds] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [tab, setTab] = useState(TAB_CONTACTS);
  const [sortOrder, setSortOrder] = useState('asc');
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const createButtonElement = useMemo(() => document.getElementById('js-new-button-container'), []);
  const [loading, setLoading] = useState(true);

  const useMobileView = useMediaQuery({
    query: '(max-width: 992px)'
  });

  const search = useCallback((input, page, order, tags) => {
    setLoading(true);
    return searchContacts(input, page, { name: order }, tags, true).then((res) => {
      setContacts(res.contacts.hits);
      setContactsTotal(res.contacts.total);
      setCompanies(res.companies.hits);
      setCompaniesTotal(res.companies.total);
      setLoading(false);
    });
  }, []);

  const debounceSearch = useCallback(debounce(search, 300), []);
  const isAllSelected = tab === TAB_CONTACTS ? (contacts.length === checkedContactIds.length) || false : (companies.length === checkedContactIds.length) || false;

  useEffect(() => {
    setPage(1);
    setCheckedContactIds([]);
  }, [tab, tagFilters, searchInput, sortOrder]);

  useEffect(() => {
    if (searchInput && searchInput.length < 3) return;

    debounceSearch(searchInput ? searchInput : '*', page, sortOrder, tagFilters);
  }, [searchInput, sortOrder, tagFilters, page]);

  useEffect(() => {
    if (!useMobileView && !selectedContactId && contacts.length > 0) setSelectedContactId(contacts[0].id);
  }, [contacts]);

  const handleInputChange = useCallback((e) => {
    const { value } = e.target;
    setSearchInput(value);
  }, []);

  const handleContactDelete = useCallback((contactId, contactType) => {
    deleteContact(contactId).then(() => {
      // Reset selected contact since this only happens from a selected contact
      setSelectedContactId(undefined);
      if (contactType === 'Connect::Company') {
        setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== contactId));
      } else {
        setContacts(prevContacts => prevContacts.filter(contact => contact.id !== contactId));
      }
    });
  }, []);

  const handleContactUpdate = useCallback((contact) => {
    setContacts(prevContacts => prevContacts.map(prev => prev.id === contact.id ? contact : prev));
    setCompanies(prevCompanies => prevCompanies.map(prev => prev.id === contact.id ? contact : prev));
  }, []);

  const handlePushContact = (contact) => {
    if (contact.type == CONNECT_PERSON_TYPE) {
      setContacts([contact, ...contacts]);
      setContactsTotal(contactsTotal + 1);
    } else if (contact.type == CONNECT_COMPANY_TYPE) {
      setCompanies([contact, ...companies]);
      setCompaniesTotal(companiesTotal + 1);
    }
  };

  const handleContactCheck = (id) => {
    if (checkedContactIds.includes(id)) {
      setCheckedContactIds(prev => prev.filter(contactId => contactId !== id));
    } else {
      setCheckedContactIds(prev => [...prev, id]);
    }
  };

  const handleSelectAll = useCallback(() => {
    if (tab === TAB_CONTACTS) {
      setCheckedContactIds(isAllSelected ? [] : contacts.map(contact => contact.id));
    } else {
      setCheckedContactIds(isAllSelected ? [] : companies.map(company => company.id));
    }
  }, [isAllSelected, contacts, companies, tab]);

  const handleDeleteContacts = useCallback(() => {
    const deletedModelString = tab === TAB_CONTACTS ? 'Contact' : 'Company';
    if (confirm(`Delete ${checkedContactIds.length} ${pluralize(deletedModelString, checkedContactIds.length)}?`)) {
      deleteContacts(checkedContactIds).then(() => {
        if (tab === TAB_CONTACTS) {
          setContacts(contacts.filter(c => !checkedContactIds.includes(c.id)));
          setContactsTotal(prev => prev - checkedContactIds.length);
        } else if (tab === TAB_COMPANIES) {
          setCompanies(companies.filter(c => !checkedContactIds.includes(c.id)));
          setCompaniesTotal(prev => prev - checkedContactIds.length);
        }
        setCheckedContactIds([]);
      });
    }
  }, [checkedContactIds, companies, contacts, tab]);

  const renderActionsRow = () => {
    return (
      <ListGroup.Item>
        <div className="d-flex gap-3 align-items-center d-flex flex-grow-1">
          <Form.Check
            checked={isAllSelected}
            id="select_all_contacts"
            label="Select All"
            onChange={handleSelectAll}
          />
          <Button disabled={checkedContactIds.length === 0} size="sm" variant="danger" onClick={handleDeleteContacts}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </ListGroup.Item>
    );
  };

  const renderContactList = useCallback((contacts) => {
    if (loading) return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );

    if (contacts.length == 0) return (
      <EmptyState icon={faCircleExclamation} title="No Results Found" />
    );

    return contacts.map(contact => (
      <ListGroup.Item
        className="py-4 fw-semibold hover-toggle-parent d-flex flex-grow-1"
        key={contact.id}
      >
        <Row className="align-items-center" style={{ minHeight: '40px' }}>
          <Col>
            <Row className="align-items-center">
              <Col lg="auto" xs={12}>
                <div className="d-inline-flex gap-2">
                  <Form.Check
                    checked={checkedContactIds.includes(contact.id)}
                    type="checkbox"
                    onChange={() => handleContactCheck(contact.id)}
                  />
                  <div className="clickable privacy-mask" onClick={() => setSelectedContactId(contact.id)}>
                    <strong>{contact.name}</strong>
                    {(contact.company || contact.jobTitle) && (
                      <div className="text-muted">
                        <small>{contact?.company?.name}</small>
                        <small>{contact.jobTitle && (`${contact.company ? ' •' : ''} ${contact.jobTitle}`)}</small>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col>
                <div className="d-inline-flex">
                  <ConnectTagManager
                    buttonProperties={{
                      buttonClasses: `${useMobileView ? 'd-inline' : 'hover-d-inline'}`,
                      variant: 'text-secondary',
                      iconLeft: <FontAwesomeIcon className="me-2" icon={faTag} />
                    }}
                    initialTags={contact.tags}
                    recordId={contact.id}
                    recordType={CONTACT_MODEL}
                  />
                </div>
              </Col>
            </Row>
          </Col>
          {useMobileView && (
            <Col className="align-self-center" xs="auto">
              <FontAwesomeIcon icon={faChevronRight} />
            </Col>
          )}
        </Row>
      </ListGroup.Item>
    ));
  }, [checkedContactIds, loading]);

  const currentTabTotal = (tab === TAB_CONTACTS ? contactsTotal : companiesTotal) || 0;

  return (
    <>
      <Row>
        <Col className={useMobileView && selectedContactId ? 'd-none' : ''} lg={5}>
          <Card>
            <Card.Body className="d-flex flex-column gap-4">
              <Row>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </InputGroup.Text>
                    <Form.Control
                      autoFocus
                      placeholder="Search for a contact by name"
                      type="text"
                      value={searchInput}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </Col>
                <Col xs="auto">
                  <Dropdown className="border rounded">
                    <Dropdown.Toggle variant="link">
                      Name
                      <FontAwesomeIcon icon={sortOrder == 'asc' ? faArrowDownAZ : faArrowUpAZ} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setSortOrder('asc')}>
                        Name
                        <FontAwesomeIcon icon={faArrowDownAZ} />
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => setSortOrder('desc')}>
                        Name
                        <FontAwesomeIcon icon={faArrowUpAZ} />
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {tagFilters.map(tag => (
                  <div className="d-flex align-items-stretch" key={tag}>
                    <Badge
                      bg=""
                      className="bg-connect-tag text-body rounded-end-0 clickable"
                    >
                      <small className="fw-normal">{tag}</small>
                    </Badge>
                    <Badge
                      bg=""
                      className="bg-connect-tag text-body rounded-start-0 clickable"
                      onClick={() => setTagFilters(tagFilters.filter(t => t != tag))}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Badge>
                  </div>
                ))}
                <TagPicker
                  buttonProperties={{
                    buttonClasses: 'p-0',
                    extraLabel: 'Filter',
                    iconRight: <FontAwesomeIcon icon={faPlus} />,
                    variant: 'text',
                  }}
                  excludeNames={tagFilters}
                  onTagSelected={name => setTagFilters([...tagFilters, name])}
                />
              </div>
            </Card.Body>
            <Nav activeKey={tab} variant="tabs" onSelect={key => key && setTab(key)}>
              <Nav.Item>
                <Nav.Link className="border-start-0 py-2" eventKey={TAB_CONTACTS}>
                  Contacts ({contactsTotal})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link className="py-2" eventKey={TAB_COMPANIES}>
                  Companies ({companiesTotal})
                </Nav.Link>
              </Nav.Item>
            </Nav>
            {tab === TAB_CONTACTS && (
              <ListGroup className="border-top-0" variant="flush">
                {renderActionsRow()}
                {renderContactList(contacts)}
              </ListGroup>
            )}
            {tab === TAB_COMPANIES && (
              <ListGroup className="border-top-0" variant="flush">
                {renderActionsRow()}
                {renderContactList(companies)}
              </ListGroup>
            )}
            {currentTabTotal > PER_PAGE && (
              <Card.Footer className="d-flex justify-content-center">
                <Paginator
                  currentPage={page}
                  perPage={PER_PAGE}
                  total={currentTabTotal}
                  onSelect={setPage}
                />
              </Card.Footer>
            )}
          </Card>
        </Col>
        <Col className={!useMobileView || selectedContactId ? '' : 'd-none'} lg={7}>
          <Card style={{ minHeight: '100px' }}>
            <Card.Header className="d-lg-none">
              <Button variant="link" onClick={() => setSelectedContactId(undefined)}>
                <FontAwesomeIcon icon={faChevronLeft} />Back
              </Button>
            </Card.Header>
            {selectedContactId && (
              <ContactDetails
                id={selectedContactId}
                profile={profile}
                onDelete={handleContactDelete}
                onUpdate={handleContactUpdate}
              />
            )}
          </Card>
        </Col>
      </Row>
      {showCreateModal && (
        <CreateModal defaultTab={tab} onHide={() => setShowCreateModal(false)} onPushContact={handlePushContact} />
      )}
      {createButtonElement && createPortal((
        <>
          <ImportHandler />
          <Button variant="secondary" onClick={() => setShowCreateModal(true)}>
            <FontAwesomeIcon icon={faUserPlus} />
            New Contact
          </Button>
        </>
      ), createButtonElement)}
    </>
  );
}
